/**
 * Central de Gestão TPL — agregação frontend dos dados existentes.
 * Não altera cálculos, APIs ou regras de negócio.
 */

import type { EnterpriseAction } from '../../../services/actionService';
import type { Indicator, IndicatorSnapshot } from '../../../types/indicator';
import {
  computeCadeiaChainInsights,
  type CadeiaLeaderScore,
} from './cadeiaChainInsights';
import { resolveAndonLevelForCargo, ANDON_ESCALATION_LEVELS } from './andonEscalation';
import { computeIel, IEL_CLASSIFICATION_LABELS, type IelClassification } from './ielService';
import { computeTplOeeMetrics } from './lstOeeMetrics';
import {
  buildPlannerBlocks,
  PLANNER_WEEKDAYS,
  type PlannerBlock,
} from './tplPlannerBlocks';
import {
  computeTplMaturityModel,
  lastNDaysDates,
  type TplMaturityBand,
} from './tplMaturityModel';
import { toTplMaturityPresentation } from '../utils/tplMaturityPresentation';
import { formatExceptionCount } from './tplSeverityStyles';
import { templateHasLinkedIndicator } from '../utils/lstIndicator';
import { filterTemplatesForUser } from '../utils/lstTemplateScope';
import type { LstCargo, LstExecucao, LstLideradoAderencia, LstTemplate } from '../types';

export type HubTargetScreen =
  | 'central-gestao'
  | 'meu-padrao'
  | 'cadeia-ajuda'
  | 'construtor'
  | 'auditoria'
  | 'diagnostico'
  | 'configuracao';

export type HubStatusTone = 'normal' | 'atencao' | 'critico';

export interface HubHealthCard {
  key: 'iel' | 'maturidade' | 'disciplina' | 'conformidade';
  label: string;
  subtitle?: string;
  detail?: string | null;
  value: string;
  status: HubStatusTone;
  statusLabel: string;
  trend: number | null;
  trendLabel: string;
}

export interface HubRiskItem {
  id: string;
  label: string;
  impact: number;
  tone: HubStatusTone;
  targetScreen: HubTargetScreen;
}

export interface HubCriticalLeader {
  id: string;
  name: string;
  disciplinaPct: number;
  conformidadePct: number;
  statusLabel: string;
  responsible: string;
  tone: HubStatusTone;
}

export interface HubPlannerConflict {
  id: string;
  leaderId: string;
  leaderName: string;
  dayLabel: string;
  hour: string;
  priority: HubStatusTone;
  routineTitles: string[];
}

export interface HubEscalatedChain {
  leaderId: string;
  leaderName: string;
  currentLayer: string;
  timeOpenLabel: string;
  slaLabel: string;
  statusLabel: string;
  tone: HubStatusTone;
}

export interface HubActionSummary {
  criticas: number;
  emAtraso: number;
  pendentes: number;
}

export interface TplManagementHubData {
  overallTone: HubStatusTone;
  headline: string;
  healthCards: HubHealthCard[];
  risks: HubRiskItem[];
  criticalLeaders: HubCriticalLeader[];
  plannerConflicts: HubPlannerConflict[];
  totalConflicts: number;
  escalatedChains: HubEscalatedChain[];
  actionSummary: HubActionSummary;
  executiveContext: {
    ielScore: number;
    maturityScore: number;
    disciplinaPct: number;
    weekLossCount: number;
    prevDisciplinaPct: number | null;
    criticalLossCount: number;
  };
}

export interface ComputeTplManagementHubInput {
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  weekDates: string[];
  monthDates: string[];
  today: string;
  viewerCargo: LstCargo;
  chainRows: LstLideradoAderencia[];
  leaders: Array<{ id: string; name: string; cargo: LstCargo }>;
  orgActions: EnterpriseAction[];
  indicators?: Indicator[];
  indicatorSnapshots?: IndicatorSnapshot[];
}

function previousPeriodDates(dates: string[]): string[] {
  if (dates.length === 0) return [];
  const [y, m, d] = dates[0].split('-').map(Number);
  const start = new Date(y, m - 1, d);
  start.setDate(start.getDate() - dates.length);
  const out: string[] = [];
  for (let i = 0; i < dates.length; i++) {
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    out.push(toLocalISO(dt));
  }
  return out;
}

function toLocalISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toneFromPct(pct: number, warn = 70, crit = 50): HubStatusTone {
  if (pct < crit) return 'critico';
  if (pct < warn) return 'atencao';
  return 'normal';
}

function toneFromIel(c: IelClassification): HubStatusTone {
  if (c === 'critico') return 'critico';
  if (c === 'atencao') return 'atencao';
  return 'normal';
}

function toneFromMaturity(b: TplMaturityBand): HubStatusTone {
  if (b === 'critico') return 'critico';
  if (b === 'emergente') return 'atencao';
  return 'normal';
}

function formatTrend(delta: number | null, unit = 'p.p.'): string {
  if (delta === null || delta === 0) return 'Estável';
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta} ${unit}`;
}

function gravidadeToTone(g: CadeiaLeaderScore['gravidade']): HubStatusTone {
  if (g === 'critico') return 'critico';
  if (g === 'atencao') return 'atencao';
  return 'normal';
}

function computePlannerConflicts(
  leaders: Array<{ id: string; name: string; cargo: LstCargo }>,
  templates: LstTemplate[],
  weekDates: string[],
): HubPlannerConflict[] {
  const dayLabel = new Map(PLANNER_WEEKDAYS.map((d) => [d.id, d.label]));
  const out: HubPlannerConflict[] = [];

  for (const leader of leaders) {
    const scoped = filterTemplatesForUser(templates, leader.cargo, leader.id);
    const blocks = buildPlannerBlocks(scoped, weekDates);
    const byCell = new Map<string, PlannerBlock[]>();
    for (const b of blocks) {
      const key = `${b.day}-${b.hour}`;
      const arr = byCell.get(key) ?? [];
      arr.push(b);
      byCell.set(key, arr);
    }
    for (const [key, arr] of byCell) {
      if (arr.length <= 1) continue;
      const [dayId, hour] = key.split('-');
      out.push({
        id: `${leader.id}-${key}`,
        leaderId: leader.id,
        leaderName: leader.name,
        dayLabel: dayLabel.get(Number(dayId)) ?? dayId,
        hour,
        priority: arr.length >= 3 ? 'critico' : 'atencao',
        routineTitles: arr.map((b) => b.title),
      });
    }
  }

  return out.sort((a, b) => {
    const pa = a.priority === 'critico' ? 2 : 1;
    const pb = b.priority === 'critico' ? 2 : 1;
    return pb - pa || b.routineTitles.length - a.routineTitles.length;
  });
}

function summarizeActions(actions: EnterpriseAction[], today: string): HubActionSummary {
  const open = actions.filter((a) => a.status !== 'completed' && a.status !== 'cancelled');
  const lstOpen = open.filter((a) => {
    const src = (a.metadata?.source as string | undefined) ?? '';
    return src === 'lst' || src === 'lst_tpl' || a.title.toLowerCase().includes('tpl');
  });
  const pool = lstOpen.length > 0 ? lstOpen : open;

  return {
    criticas: pool.filter((a) => a.priority === 'high' || a.priority === 'urgent').length,
    emAtraso: pool.filter(
      (a) =>
        (a.sla_status === 'overdue' || (a.due_date && a.due_date < today)) &&
        a.status !== 'completed',
    ).length,
    pendentes: pool.filter((a) => a.status === 'pending').length,
  };
}

function buildRisks(input: {
  disciplina: number;
  conformidade: number;
  maturityRisks: string[];
  conflictCount: number;
  escalatedCount: number;
  templatesWithoutIndicator: number;
  criticalMissed: number;
}): HubRiskItem[] {
  const items: HubRiskItem[] = [];

  if (input.disciplina < 70) {
    items.push({
      id: 'disciplina-baixa',
      label: `Disciplina abaixo de 70% (${input.disciplina}%)`,
      impact: 100 - input.disciplina,
      tone: input.disciplina < 50 ? 'critico' : 'atencao',
      targetScreen: 'diagnostico',
    });
  }
  if (input.templatesWithoutIndicator > 0) {
    items.push({
      id: 'sem-indicador',
      label: `${input.templatesWithoutIndicator} rotina(s) sem indicador vinculado`,
      impact: 60 + input.templatesWithoutIndicator,
      tone: 'atencao',
      targetScreen: 'configuracao',
    });
  }
  if (input.conflictCount > 0) {
    items.push({
      id: 'planner-conflitos',
      label: `${input.conflictCount} conflito(s) de agenda no Planner`,
      impact: 50 + input.conflictCount * 10,
      tone: input.conflictCount >= 3 ? 'critico' : 'atencao',
      targetScreen: 'construtor',
    });
  }
  if (input.escalatedCount > 0) {
    items.push({
      id: 'cadeias-escalonadas',
      label: `${input.escalatedCount} cadeia(s) de ajuda escalonada(s)`,
      impact: 70 + input.escalatedCount * 15,
      tone: 'critico',
      targetScreen: 'cadeia-ajuda',
    });
  }
  if (input.criticalMissed > 0) {
    items.push({
      id: 'rotinas-criticas',
      label: `${input.criticalMissed} rotina(s) crítica(s) sem execução na semana`,
      impact: 80 + input.criticalMissed * 5,
      tone: 'critico',
      targetScreen: 'auditoria',
    });
  }
  if (input.conformidade < 70) {
    items.push({
      id: 'conformidade-baixa',
      label: `Conformidade abaixo de 70% (${input.conformidade}%)`,
      impact: 90 - input.conformidade,
      tone: 'atencao',
      targetScreen: 'diagnostico',
    });
  }

  for (const [i, r] of input.maturityRisks.slice(0, 3).entries()) {
    items.push({
      id: `maturity-risk-${i}`,
      label: r,
      impact: 40 - i,
      tone: r.toLowerCase().includes('crític') || r.toLowerCase().includes('vencid') ? 'critico' : 'atencao',
      targetScreen: 'diagnostico',
    });
  }

  return items.sort((a, b) => b.impact - a.impact).slice(0, 5);
}

export function computeTplManagementHub(input: ComputeTplManagementHubInput): TplManagementHubData {
  const {
    templates,
    execucoes,
    weekDates,
    monthDates,
    today,
    viewerCargo,
    chainRows,
    leaders,
    orgActions,
    indicators = [],
    indicatorSnapshots = [],
  } = input;

  const activeTemplates = templates.filter((t) => t.active);
  const prevDates = previousPeriodDates(weekDates);
  const sustainDates = lastNDaysDates(90, today);

  const oee = computeTplOeeMetrics(activeTemplates, execucoes, weekDates);
  const prevOee =
    prevDates.length > 0
      ? computeTplOeeMetrics(activeTemplates, execucoes, prevDates)
      : null;

  const iel = computeIel({
    templates: activeTemplates,
    execucoes,
    dates: weekDates,
    previousDates: prevDates,
    indicators,
    indicatorSnapshots,
    includeTrend: true,
  });

  const maturityModel = computeTplMaturityModel({
    templates: activeTemplates,
    execucoes,
    weekDates,
    monthDates,
    sustainDates,
    today,
    indicators,
    indicatorSnapshots,
    correctiveActions: orgActions,
  });
  const maturity = toTplMaturityPresentation(maturityModel);

  const chainInsights = computeCadeiaChainInsights(chainRows, viewerCargo);
  const viewerLevel = resolveAndonLevelForCargo(viewerCargo);
  const viewerMeta = ANDON_ESCALATION_LEVELS.find((l) => l.level === viewerLevel)!;

  const hubLeaders =
    leaders.length > 0
      ? leaders
      : chainRows.map((r) => ({
          id: r.member.id,
          name: r.member.name,
          cargo: r.cargo,
        }));

  const plannerConflicts = computePlannerConflicts(hubLeaders, templates, weekDates);

  const criticalLeaders: HubCriticalLeader[] = chainInsights.criticalLeaders
    .filter((c) => c.gravidade !== 'normal')
    .slice(0, 8)
    .map((c) => {
      const leaderTemplates = filterTemplatesForUser(templates, c.row.cargo, c.row.member.id);
      const leaderExec = execucoes.filter((e) => e.user_id === c.row.member.id);
      const leaderOee = computeTplOeeMetrics(leaderTemplates, leaderExec, weekDates);
      const andonStat = chainInsights.andonLevels.find((l) => l.level === viewerLevel);
      return {
        id: c.row.member.id,
        name: c.row.member.name,
        disciplinaPct: leaderOee.disciplinaPct,
        conformidadePct: leaderOee.conformidadePct,
        statusLabel: c.chipReason ?? 'Em atenção',
        responsible:
          andonStat?.responsibilityLabel === 'Agir agora'
            ? viewerMeta.shortLabel
            : ANDON_ESCALATION_LEVELS.find((l) => l.level === Math.min(viewerLevel + 1, 3))?.shortLabel ??
              viewerMeta.shortLabel,
        tone: gravidadeToTone(c.gravidade),
      };
    });

  const escalatedChains: HubEscalatedChain[] = chainInsights.criticalLeaders
    .filter((c) => c.gravidade !== 'normal')
    .slice(0, 6)
    .map((c) => {
      const stat = chainInsights.andonLevels.find((l) => l.responsibility === 'agir_agora');
      const layer = stat?.shortLabel ?? viewerMeta.shortLabel;
      return {
        leaderId: c.row.member.id,
        leaderName: c.row.member.name,
        currentLayer: layer,
        timeOpenLabel: c.piorDiaLabel ?? 'Semana atual',
        slaLabel: `${stat?.toleranceMinutes ?? viewerMeta.toleranceMinutes} min`,
        statusLabel: stat?.responsibilityLabel ?? 'Monitorando',
        tone: gravidadeToTone(c.gravidade),
      };
    });

  const templatesWithoutIndicator = activeTemplates.filter(
    (t) => t.is_critical && !templateHasLinkedIndicator(t),
  ).length;

  const criticalMissed = activeTemplates.filter((t) => t.is_critical).length > 0
    ? execucoes.filter((e) => {
        const tpl = activeTemplates.find((t) => t.id === e.template_id);
        return tpl?.is_critical && e.status === 'nao_realizado' && weekDates.includes(e.data);
      }).length
    : 0;

  const risks = buildRisks({
    disciplina: oee.disciplinaPct,
    conformidade: oee.conformidadePct,
    maturityRisks: maturityModel.risks,
    conflictCount: plannerConflicts.length,
    escalatedCount: escalatedChains.filter((e) => e.tone !== 'normal').length,
    templatesWithoutIndicator,
    criticalMissed,
  });

  const healthCards: HubHealthCard[] = [
    {
      key: 'iel',
      label: 'IEL',
      value: `${iel.score}/100`,
      status: toneFromIel(iel.classification),
      statusLabel: IEL_CLASSIFICATION_LABELS[iel.classification].toUpperCase(),
      trend: iel.trend,
      trendLabel: formatTrend(iel.trend),
    },
    {
      key: 'maturidade',
      label: 'Maturidade TPL',
      value: `${maturity.score}/100`,
      status: toneFromMaturity(maturity.band),
      statusLabel: maturity.bandLabel.toUpperCase(),
      trend: null,
      trendLabel: 'Semana atual',
    },
    {
      key: 'disciplina',
      label: 'Disciplina',
      subtitle: 'Comparecimento às rotinas',
      detail: formatExceptionCount(
        execucoes.filter(
          (e) => e.status === 'nao_realizado' && weekDates.includes(e.data),
        ).length,
        'não realizada',
        'não realizadas',
      ),
      value: `${oee.disciplinaPct}%`,
      status: toneFromPct(oee.disciplinaPct),
      statusLabel: toneFromPct(oee.disciplinaPct) === 'normal' ? 'NORMAL' : toneFromPct(oee.disciplinaPct) === 'atencao' ? 'ATENÇÃO' : 'CRÍTICO',
      trend: prevOee ? oee.disciplinaPct - prevOee.disciplinaPct : null,
      trendLabel: formatTrend(prevOee ? oee.disciplinaPct - prevOee.disciplinaPct : null),
    },
    {
      key: 'conformidade',
      label: 'Conformidade',
      subtitle: 'Execução correta',
      detail: formatExceptionCount(oee.counts.desvio, 'desvio', 'desvios'),
      value: `${oee.conformidadePct}%`,
      status: toneFromPct(oee.conformidadePct),
      statusLabel: toneFromPct(oee.conformidadePct) === 'normal' ? 'NORMAL' : toneFromPct(oee.conformidadePct) === 'atencao' ? 'ATENÇÃO' : 'CRÍTICO',
      trend: prevOee ? oee.conformidadePct - prevOee.conformidadePct : null,
      trendLabel: formatTrend(prevOee ? oee.conformidadePct - prevOee.conformidadePct : null),
    },
  ];

  const actionSummary = summarizeActions(orgActions, today);

  const overallTone: HubStatusTone =
    risks.some((r) => r.tone === 'critico') || healthCards.some((c) => c.status === 'critico')
      ? 'critico'
      : risks.length > 0 || healthCards.some((c) => c.status === 'atencao')
        ? 'atencao'
        : 'normal';

  const weekLossCount = execucoes.filter(
    (e) => e.status === 'nao_realizado' && weekDates.includes(e.data),
  ).length;

  const actionCount =
    actionSummary.criticas + actionSummary.emAtraso + actionSummary.pendentes;

  const headline =
    overallTone === 'normal'
      ? 'Liderança dentro do esperado — manter ritmo de gemba.'
      : overallTone === 'critico'
        ? `${risks[0]?.label ?? 'Situação crítica'} — ação imediata necessária.`
        : `${risks.length} risco(s) ativo(s) — gestão por exceção recomendada.`;

  return {
    overallTone,
    headline,
    healthCards,
    risks,
    criticalLeaders,
    plannerConflicts,
    totalConflicts: plannerConflicts.length,
    escalatedChains,
    actionSummary,
    executiveContext: {
      ielScore: iel.score,
      maturityScore: maturity.score,
      disciplinaPct: oee.disciplinaPct,
      weekLossCount,
      prevDisciplinaPct: prevOee ? prevOee.disciplinaPct : null,
      criticalLossCount: criticalMissed,
    },
  };
}
